import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { api } from "@/lib/api";

const SERVICES = [
  "Social Media Management",
  "Brand Strategy & Identity",
  "Content Direction",
  "Paid Media",
  "Web Design",
  "Something Else",
];

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    business: "",
    service: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill in name, email and a short message.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/inquiries", {
        name: form.name.trim(),
        email: form.email.trim(),
        business: form.business.trim() || null,
        service: form.service || null,
        message: form.message.trim(),
      });
      setSubmitted(true);
      toast.success("Received — Whitney will be in touch within 48 hours.");
      setForm({ name: "", email: "", business: "", service: "", message: "" });
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        err?.response?.data?.[0]?.msg ||
        "Something went wrong. Please try again or email us directly.";
      toast.error(typeof msg === "string" ? msg : "Please check your entries.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="py-24 md:py-40 bg-[#F2EEE9] border-t border-[#E5E0D8]"
    >
      <div className="max-w-7xl mx-auto w-full px-6 md:px-12 grid grid-cols-12 gap-8 md:gap-16">
        <div className="col-span-12 md:col-span-5">
          <div className="flex items-center gap-3">
            <span className="w-8 h-px bg-[#1C1B1A]"></span>
            <span className="font-mono-label text-[#635F59]">
              Get in touch
            </span>
          </div>
          <h2 className="mt-6 font-serif-display text-4xl md:text-5xl lg:text-6xl leading-[1.02] tracking-tight">
            Tell us about
            <br />
            your <span className="italic text-[#B4A087]">brand.</span>
          </h2>
          <p className="mt-6 text-base md:text-lg text-[#635F59] leading-relaxed max-w-md">
            Book a <span className="italic">free consultation</span> or send us
            a note. We reply to every enquiry personally, usually within 48
            hours.
          </p>
          <div className="mt-10 space-y-4">
            <div>
              <p className="font-mono-label text-[#635F59]">Email</p>
              <a
                href="mailto:info@whitneyjeanco.ca"
                data-testid="contact-email-link"
                className="mt-1 inline-block font-serif-display text-2xl md:text-3xl border-b border-[#1C1B1A]/40 hover:border-[#1C1B1A] transition-colors"
              >
                info@whitneyjeanco.ca
              </a>
            </div>
            <div>
              <p className="font-mono-label text-[#635F59]">Instagram</p>
              <a
                href="https://www.instagram.com/whitneyjeanco/"
                target="_blank"
                rel="noreferrer"
                data-testid="contact-instagram-link"
                className="mt-1 inline-block font-serif-display text-2xl md:text-3xl border-b border-[#1C1B1A]/40 hover:border-[#1C1B1A] transition-colors"
              >
                @whitneyjeanco
              </a>
            </div>
          </div>
        </div>

        <div className="col-span-12 md:col-span-7">
          {submitted ? (
            <motion.div
              data-testid="contact-success-state"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="border border-[#1C1B1A] bg-[#FBF9F6] p-10 md:p-14"
            >
              <span className="font-mono-label text-[#B4A087]">
                Received
              </span>
              <h3 className="mt-6 font-serif-display text-3xl md:text-4xl leading-tight">
                Thank you — your note is in.
              </h3>
              <p className="mt-4 text-[#635F59] leading-relaxed max-w-lg">
                Whitney will read it personally and reply within 48 hours.
                In the meantime, feel free to explore our selected work.
              </p>
              <button
                type="button"
                onClick={() => setSubmitted(false)}
                data-testid="contact-submit-another"
                className="mt-8 inline-flex items-center gap-2 text-sm border-b border-[#1C1B1A]/40 hover:border-[#1C1B1A] pb-1 transition-colors"
              >
                Send another enquiry →
              </button>
            </motion.div>
          ) : (
            <form
              onSubmit={submit}
              data-testid="contact-form"
              className="space-y-8"
              noValidate
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <label className="block">
                  <span className="font-mono-label text-[#635F59]">
                    Your Name
                  </span>
                  <input
                    data-testid="contact-form-name-input"
                    type="text"
                    className="wj-input mt-3"
                    placeholder="Whitney Jean"
                    value={form.name}
                    onChange={onChange("name")}
                    required
                  />
                </label>
                <label className="block">
                  <span className="font-mono-label text-[#635F59]">Email</span>
                  <input
                    data-testid="contact-form-email-input"
                    type="email"
                    className="wj-input mt-3"
                    placeholder="you@brand.com"
                    value={form.email}
                    onChange={onChange("email")}
                    required
                  />
                </label>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <label className="block">
                  <span className="font-mono-label text-[#635F59]">
                    Business / Brand
                  </span>
                  <input
                    data-testid="contact-form-business-input"
                    type="text"
                    className="wj-input mt-3"
                    placeholder="Maison Lume"
                    value={form.business}
                    onChange={onChange("business")}
                  />
                </label>
                <label className="block">
                  <span className="font-mono-label text-[#635F59]">
                    Interested In
                  </span>
                  <select
                    data-testid="contact-form-service-select"
                    className="wj-input mt-3 appearance-none pr-6 bg-transparent cursor-pointer"
                    value={form.service}
                    onChange={onChange("service")}
                  >
                    <option value="">Select a service…</option>
                    {SERVICES.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                </label>
              </div>

              <label className="block">
                <span className="font-mono-label text-[#635F59]">
                  Tell us about your project
                </span>
                <textarea
                  data-testid="contact-form-message-input"
                  className="wj-input mt-3 min-h-[140px] resize-none"
                  rows={5}
                  placeholder="A few sentences on where you are, and where you want to go…"
                  value={form.message}
                  onChange={onChange("message")}
                  required
                />
              </label>

              <div className="pt-4 flex flex-col sm:flex-row sm:items-center gap-6">
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="contact-form-submit-button"
                  className="inline-flex items-center justify-center gap-3 bg-[#1C1B1A] text-[#FBF9F6] px-8 py-5 text-sm hover:bg-[#33312E] transition-colors disabled:opacity-60 disabled:cursor-not-allowed w-full sm:w-auto"
                >
                  {loading ? "Sending…" : "Book Free Consultation"}
                  <span aria-hidden>→</span>
                </button>
                <p className="text-xs text-[#635F59] leading-relaxed max-w-xs">
                  By submitting you agree to be contacted about your enquiry.
                  We never share your details.
                </p>
              </div>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}
